import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useHideCrisp } from "@/hooks/use-hide-crisp";
import { DEFAULT_ACCENT_COLOR } from "@/lib/brand-colors";
import { useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { MapPin, BedDouble, ChevronRight, Check, Phone, Mail, User } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import type { ReferralLink } from "@shared/schema";
import { isPrimaryRoom } from "@shared/fees";
import { DARK_MAP_STYLES, useIsDarkClass } from "@/lib/map-styles";

type PublicRoom = {
  id: number;
  roomNumber: string;
  status: string;
  currentPrice: string | null;
  bathroomType: string | null;
  roomType: string | null;
  roomTitle: string | null;
  shadowOfRoomId: number | null;
};

type PublicProperty = {
  id: number;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  minWeeklyPrice: string | null;
};

type PublicData = {
  property: PublicProperty;
  rooms: PublicRoom[];
  images: Array<{ imageUrl: string }>;
};

type PublicSettings = {
  headline: string;
  description: string | null;
  tagline: string | null;
  ctaButtonText: string;
  contactPhone: string | null;
  contactEmail: string | null;
  accentColor: string;
  footerText: string | null;
  brandName: string | null;
  brandLogoUrl: string | null;
};

type PriceMode = "weekly" | "biweekly" | "monthly";

function getWeeklyPrice(room: PublicRoom): number {
  if (room.currentPrice) return parseFloat(room.currentPrice);
  return 0;
}

function formatPrice(weekly: number, mode: PriceMode): string {
  if (weekly <= 0) return "";
  if (mode === "weekly") return `$${Math.round(weekly)}/wk`;
  if (mode === "biweekly") return `$${Math.round(weekly * 2)}/2wk`;
  return `$${Math.round(weekly * 4.33)}/mo`;
}

function convertPrice(weekly: number, mode: PriceMode): number {
  if (mode === "weekly") return Math.round(weekly);
  if (mode === "biweekly") return Math.round(weekly * 2);
  return Math.round(weekly * 4.33);
}

type LeadForm = {
  name: string;
  email: string;
  phone: string;
  propertyId?: number | null;
  referralSource?: string | null;
};

function findReferralLink(address: string | null, links: ReferralLink[]): string {
  if (links.length === 0) return "https://padsplit.com/";
  const normalized = links.map(l => ({
    ...l,
    marketName: l.marketName || (l as unknown as { name?: string }).name || "",
  }));
  if (address) {
    const lower = address.toLowerCase();
    for (const link of normalized) {
      if (link.marketName && lower.includes(link.marketName.toLowerCase())) {
        return link.url;
      }
    }
  }
  const defaultLink = normalized.find(l => l.isDefault);
  if (defaultLink) return defaultLink.url;
  return links[0].url;
}

function createPriceMarkerSvg(price: string, color: string): string {
  const width = Math.max(60, price.length * 9 + 20);
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="36" viewBox="0 0 ${width} 36">
      <rect x="0" y="0" width="${width}" height="28" rx="14" fill="${color}" />
      <polygon points="${width/2 - 6},28 ${width/2 + 6},28 ${width/2},36" fill="${color}" />
      <text x="${width/2}" y="18" text-anchor="middle" font-family="system-ui, sans-serif" font-size="12" font-weight="700" fill="white">${price}</text>
    </svg>
  `)}`;
}

function InteractiveMap({ properties, accentColor, priceMode }: { properties: PublicProperty[]; accentColor: string; priceMode: PriceMode }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const isDark = useIsDarkClass();

  useEffect(() => {
    fetch("/api/public/maps-key")
      .then(r => r.json())
      .then(d => setApiKey(d.key || ""))
      .catch(() => setApiKey(""));
  }, []);

  useEffect(() => {
    if (!apiKey || !mapRef.current) return;
    const propsWithCoords = properties.filter(p => p.latitude && p.longitude);
    if (propsWithCoords.length === 0) return;

    if (!window.google?.maps) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.onload = () => initMap(propsWithCoords);
      document.head.appendChild(script);
    } else {
      initMap(propsWithCoords);
    }
  }, [apiKey, properties, priceMode, isDark]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setOptions({
      styles: [
        ...(isDark ? DARK_MAP_STYLES : []),
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });
  }, [isDark]);

  function initMap(propsWithCoords: PublicProperty[]) {
    if (!mapRef.current) return;
    if (!window.google?.maps) return;
    const maps = window.google.maps;

    const bounds = new maps.LatLngBounds();
    const center = { lat: propsWithCoords[0].latitude!, lng: propsWithCoords[0].longitude! };

    const map = new maps.Map(mapRef.current, {
      center,
      zoom: 10,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        ...(isDark ? DARK_MAP_STYLES : []),
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });
    mapInstanceRef.current = map;

    propsWithCoords.forEach(p => {
      const pos = { lat: p.latitude!, lng: p.longitude! };
      bounds.extend(pos);
      const weeklyPrice = p.minWeeklyPrice ? parseFloat(p.minWeeklyPrice) : 0;
      const displayPrice = weeklyPrice > 0 ? formatPrice(weeklyPrice, priceMode) : p.name.substring(0, 6);
      const markerIcon: google.maps.Icon | undefined = weeklyPrice > 0 ? {
        url: createPriceMarkerSvg(displayPrice, accentColor),
        scaledSize: new maps.Size(Math.max(60, displayPrice.length * 9 + 20), 36),
      } : undefined;
      const marker = new maps.Marker({
        position: pos,
        map,
        title: p.name,
        ...(markerIcon ? { icon: markerIcon } : {}),
      });
      const info = new maps.InfoWindow({
        content: `<div style="padding:4px"><strong style="font-size:13px">${p.name}</strong>${p.address ? `<br><span style="font-size:11px;color:#666">${p.address}</span>` : ""}${weeklyPrice > 0 ? `<br><span style="font-size:12px;font-weight:600">From ${formatPrice(weeklyPrice, priceMode)}</span>` : ""}</div>`,
      });
      marker.addListener("click", () => info.open(map, marker));
    });

    if (propsWithCoords.length > 1) {
      map.fitBounds(bounds);
    }
    setMapLoaded(true);
  }

  if (apiKey === null) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  const propsWithCoords = properties.filter(p => p.latitude && p.longitude);
  if (!apiKey || propsWithCoords.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 gap-2">
        <MapPin className="h-8 w-8 text-gray-300" />
        <p className="text-sm text-gray-400 text-center px-4">
          {properties.length > 0
            ? `${properties.length} propert${properties.length !== 1 ? "ies" : "y"} available`
            : "No properties with location data"}
        </p>
      </div>
    );
  }

  return <div ref={mapRef} className="w-full h-full" data-testid="map-container" />;
}

export default function RoomsForRent() {
  useHideCrisp();
  usePageTitle("Rooms for Rent");
  const params = useParams<{ slug?: string }>();
  const slug = params.slug || null;
  const slugQuery = slug ? `?slug=${encodeURIComponent(slug)}` : "";

  const [priceMode, setPriceMode] = useState<PriceMode>("weekly");
  const [cityFilter, setCityFilter] = useState("all");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [userSetRange, setUserSetRange] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadForm>({ name: "", email: "", phone: "" });
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);

  const dataQuery = useQuery<PublicData[]>({
    queryKey: ["/api/public/rooms", slug],
    queryFn: () => fetch(`/api/public/rooms${slugQuery}`).then(r => r.json()),
  });

  const referralLinksQuery = useQuery<ReferralLink[]>({
    queryKey: ["/api/public/referral-links", slug],
    queryFn: () => fetch(`/api/public/referral-links${slugQuery}`).then(r => r.json()),
    retry: false,
  });

  const settingsQuery = useQuery<PublicSettings>({
    queryKey: ["/api/public/marketing-settings", slug],
    queryFn: () => fetch(`/api/public/marketing-settings${slugQuery}`).then(r => r.json()),
    retry: false,
  });

  const leadMutation = useMutation({
    mutationFn: async (data: LeadForm) =>
      apiRequest("POST", `/api/public/direct-inquiry${slugQuery}`, data),
    onSuccess: () => {
      setLeadSubmitted(true);
    },
  });

  const allData = dataQuery.data ?? [];
  const referralLinks = referralLinksQuery.data ?? [];
  const siteSettings = settingsQuery.data;
  const headline = siteSettings?.headline || "Rooms for Rent";
  const description = siteSettings?.description || null;
  const tagline = siteSettings?.tagline || "Start by choosing a home — then pick an available room inside.";
  const ctaText = siteSettings?.ctaButtonText || "Start Free Application";
  const contactPhone = siteSettings?.contactPhone || null;
  const contactEmail = siteSettings?.contactEmail || null;
  const footerText = siteSettings?.footerText || "PadPulse helps PadSplit hosts manage and grow their rental portfolio.";
  const brandName = siteSettings?.brandName || null;
  const brandLogoUrl = siteSettings?.brandLogoUrl || null;
  const accentColor = siteSettings?.accentColor || DEFAULT_ACCENT_COLOR;

  const cities = Array.from(
    new Set(
      allData.map((d) => {
        const addr = d.property.address || "";
        const parts = addr.split(",");
        return parts.length > 1 ? parts[parts.length - 2]?.trim() : null;
      }).filter(Boolean)
    )
  ) as string[];

  const filteredByCity = cityFilter === "all"
    ? allData
    : allData.filter((d) => {
        const addr = d.property.address || "";
        return addr.toLowerCase().includes(cityFilter.toLowerCase());
      });

  const propertiesWithVacant = filteredByCity.filter((d) =>
    d.rooms.some((r) => r.status === "vacant" && r.roomType !== "bathroom")
  );

  const allWeeklyPrices = useMemo(() => {
    const prices: number[] = [];
    propertiesWithVacant.forEach(d => {
      d.rooms.forEach(r => {
        if (r.status === "vacant" && r.roomType !== "bathroom" && r.currentPrice) {
          prices.push(parseFloat(r.currentPrice));
        }
      });
    });
    return prices.sort((a, b) => a - b);
  }, [propertiesWithVacant]);

  const priceMin = allWeeklyPrices.length > 0 ? Math.floor(convertPrice(allWeeklyPrices[0], priceMode)) : 0;
  const priceMax = allWeeklyPrices.length > 0 ? Math.ceil(convertPrice(allWeeklyPrices[allWeeklyPrices.length - 1], priceMode)) : 1000;

  useEffect(() => {
    if (!userSetRange && allWeeklyPrices.length > 0) {
      setPriceRange([priceMin, priceMax]);
    }
  }, [priceMin, priceMax, userSetRange, allWeeklyPrices.length]);

  const filteredByPrice = propertiesWithVacant.filter(d => {
    const vacantRooms = d.rooms.filter(r => r.status === "vacant" && r.roomType !== "bathroom" && isPrimaryRoom(r));
    const prices = vacantRooms.map(getWeeklyPrice).filter(p => p > 0);
    if (prices.length === 0) return true;
    const minConverted = convertPrice(Math.min(...prices), priceMode);
    return minConverted >= priceRange[0] && minConverted <= priceRange[1];
  });

  // Option B — send applicants through the host's booking request form so
  // they land in the Booking Requests queue. Falls back to the short inquiry
  // form when the host has no active form (or the lookup fails).
  async function openLeadModal(propertyId?: number) {
    if (slug) {
      try {
        const qs = new URLSearchParams({ slug });
        if (propertyId) qs.set("propertyId", String(propertyId));
        const res = await fetch(`/api/public/direct-booking-form?${qs.toString()}`);
        if (res.ok) {
          const form = (await res.json()) as { token?: string; propertyId?: number | null };
          if (form.token) {
            const target = `/booking-request/${encodeURIComponent(form.token)}${form.propertyId ? `?property=${form.propertyId}` : ""}`;
            window.location.assign(target);
            return;
          }
        }
      } catch {
        // fall through to the inquiry form
      }
    }
    setSelectedPropertyId(propertyId || null);
    setLeadSubmitted(false);
    setLeadForm({ name: "", email: "", phone: "" });
    setShowLeadModal(true);
  }

  function handleLeadSubmit() {
    if (!leadForm.name || !leadForm.email || !leadForm.phone) return;
    leadMutation.mutate({ ...leadForm, propertyId: selectedPropertyId, referralSource: "landing_page" });
  }

  const selectedPropertyData = selectedPropertyId
    ? filteredByPrice.find((d) => d.property.id === selectedPropertyId)
    : null;
  const referralLink = findReferralLink(selectedPropertyData?.property.address || null, referralLinks);

  const allPropertiesWithCoords = filteredByPrice.map((d) => d.property);

  const priceLabel = priceMode === "weekly" ? "/wk" : priceMode === "biweekly" ? "/2wk" : "/mo";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-gray-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          {brandLogoUrl ? (
            <img src={brandLogoUrl} alt={brandName || "Logo"} className="h-8 w-auto" data-testid="img-header-logo" />
          ) : brandName ? (
            <span className="text-xl font-bold text-gray-900" data-testid="text-header-brand">{brandName}</span>
          ) : (
            <img src={logoImg} alt="PadPulse" className="h-8 w-auto" data-testid="img-header-logo" />
          )}
          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-600">
            <a href="#rooms" className="hover:text-gray-900">Rooms</a>
            <a href="#faq" className="hover:text-gray-900">FAQ</a>
          </nav>
          <Button size="sm" onClick={() => { void openLeadModal(); }} style={{ backgroundColor: accentColor, borderColor: accentColor }} data-testid="button-header-apply">
            {ctaText}
          </Button>
        </div>
      </header>

      <main className="flex-1">
        <section className="text-center py-10 px-4 bg-white" id="rooms">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-testid="text-headline">{headline}</h1>
          {description && (
            <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto mb-2" data-testid="text-description">
              {description}
            </p>
          )}
          <p className="text-gray-500 text-sm md:text-base" data-testid="text-tagline">
            {tagline}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Rooms are listed on PadSplit. Clicking Apply will take you to the PadSplit application.
          </p>
        </section>

        <section className="bg-white border-t border-b border-gray-100 px-4 py-3">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Filters:</Label>
              <Select value={cityFilter} onValueChange={setCityFilter}>
                <SelectTrigger className="h-8 text-sm w-36" data-testid="select-city-filter">
                  <SelectValue placeholder="Select a City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">Prices:</Label>
              <div className="flex gap-1">
                {(["weekly", "biweekly", "monthly"] as PriceMode[]).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => { setPriceMode(mode); setUserSetRange(false); }}
                    className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
                      priceMode === mode
                        ? "text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    style={priceMode === mode ? { backgroundColor: accentColor } : undefined}
                    data-testid={`button-price-mode-${mode}`}
                  >
                    {mode === "biweekly" ? "BI-WEEKLY" : mode.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            {allWeeklyPrices.length > 0 && priceMin < priceMax && (
              <div className="flex items-center gap-2" data-testid="price-range-slider">
                <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide whitespace-nowrap">Range:</Label>
                <span className="text-xs text-gray-600 font-medium whitespace-nowrap">${priceRange[0]}</span>
                <input
                  type="range"
                  min={priceMin}
                  max={priceMax}
                  value={priceRange[0]}
                  onChange={(e) => { setUserSetRange(true); setPriceRange([parseInt(e.target.value), priceRange[1]]); }}
                  className="w-20 h-1.5 rounded-lg appearance-none cursor-pointer"
                  style={{ accentColor }}
                  data-testid="slider-price-min"
                />
                <input
                  type="range"
                  min={priceMin}
                  max={priceMax}
                  value={priceRange[1]}
                  onChange={(e) => { setUserSetRange(true); setPriceRange([priceRange[0], parseInt(e.target.value)]); }}
                  className="w-20 h-1.5 rounded-lg appearance-none cursor-pointer"
                  style={{ accentColor }}
                  data-testid="slider-price-max"
                />
                <span className="text-xs text-gray-600 font-medium whitespace-nowrap">${priceRange[1]}{priceLabel}</span>
              </div>
            )}
          </div>
        </section>

        <section className="px-4 py-6">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-4">
            <div className="flex-1 space-y-3">
              {dataQuery.isLoading ? (
                [...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-lg" />)
              ) : filteredByPrice.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <BedDouble className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No available rooms</p>
                  <p className="text-sm mt-1">
                    {userSetRange ? "Try adjusting your price range." : "Check back soon for new listings."}
                  </p>
                </div>
              ) : (
                filteredByPrice.map((d) => {
                  const { property, rooms, images } = d;
                  const vacantRooms = rooms.filter((r) => r.status === "vacant" && r.roomType !== "bathroom" && isPrimaryRoom(r));
                  if (vacantRooms.length === 0) return null;
                  const prices = vacantRooms.map(getWeeklyPrice).filter((p) => p > 0);
                  const minWeekly = prices.length > 0 ? Math.min(...prices) : 0;
                  const coverImage = images[0]?.imageUrl;

                  return (
                    <div
                      key={property.id}
                      className="border border-gray-200 rounded-lg overflow-hidden flex hover:shadow-md transition-shadow bg-white"
                      data-testid={`property-card-${property.id}`}
                    >
                      <div className="w-32 md:w-40 flex-shrink-0 bg-gray-100 relative" style={{ minHeight: "120px" }}>
                        {coverImage ? (
                          <img
                            src={coverImage}
                            alt={property.name}
                            className="w-full h-full object-cover absolute inset-0"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center absolute inset-0">
                            <BedDouble className="h-8 w-8 text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-4">
                        <h3 className="font-semibold text-gray-900 text-sm" data-testid={`property-name-${property.id}`}>
                          {property.name}
                        </h3>
                        {property.address && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {property.address}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          <BedDouble className="h-3 w-3 inline mr-1" />
                          {vacantRooms.length} room{vacantRooms.length !== 1 ? "s" : ""} available
                        </p>
                        {minWeekly > 0 && (
                          <p className="text-sm font-semibold text-gray-900 mt-2">
                            From {formatPrice(minWeekly, priceMode)}
                          </p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            className="text-xs h-7"
                            style={{ backgroundColor: accentColor, borderColor: accentColor }}
                            onClick={() => { void openLeadModal(property.id); }}
                            data-testid={`button-apply-${property.id}`}
                          >
                            Apply for a Room
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => { void openLeadModal(property.id); }}
                            data-testid={`button-book-${property.id}`}
                          >
                            Book a Room Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div
              className="lg:w-80 xl:w-96 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
              style={{ minHeight: "400px" }}
            >
              <InteractiveMap properties={allPropertiesWithCoords} accentColor={accentColor} priceMode={priceMode} />
            </div>
          </div>
        </section>

        <section className="bg-gray-50 border-t border-gray-200 py-12 px-4">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Start Your Free Application Today</h2>
              <p className="text-gray-500 text-sm mt-1">
                Apply online in minutes to access all available rooms. Rooms are listed via PadSplit — you'll be redirected to their platform to complete your application.
              </p>
            </div>
            <Button
              size="lg"
              className="flex-shrink-0"
              style={{ backgroundColor: accentColor, borderColor: accentColor }}
              onClick={() => { void openLeadModal(); }}
              data-testid="button-cta-apply"
            >
              {ctaText}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </section>

        <section className="py-12 px-4" id="faq">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-10">
            <div className="md:w-64 flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
            </div>
            <div className="flex-1">
              <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
                <AccordionItem value="item-1" data-testid="faq-move-in">
                  <AccordionTrigger className="text-sm font-medium text-left">
                    How Soon Can I Move In?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600">
                    Any room shown on this site is available now. After you complete the free application and screening, we can review your application quickly — often the same day — so you can choose a room, sign the Membership Agreement, and get move-in instructions.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2" data-testid="faq-included">
                  <AccordionTrigger className="text-sm font-medium text-left">
                    What's Included In My Membership?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600">
                    Typical rooms come fully furnished with a bed, dresser, and desk. Some rooms come with upgraded amenities, which may or may not cost extra. Common areas like the kitchen, bathrooms, and living room are shared. Most properties include high-speed WiFi and regular cleaning of common areas.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3" data-testid="faq-minimum-stay">
                  <AccordionTrigger className="text-sm font-medium text-left">
                    Do I Need A Long-Term Lease? Is There A Minimum Stay?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600">
                    There's no long-term lease required. The minimum stay is typically one month, but flexible arrangements may be available. Rooms are billed weekly through PadSplit.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4" data-testid="faq-fees">
                  <AccordionTrigger className="text-sm font-medium text-left">
                    What Fees Should I Expect To Move In?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600">
                    Move-in fees vary by property. You can expect a first week's payment and potentially a move-in fee. All fees are displayed transparently during the PadSplit application process.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5" data-testid="faq-tour">
                  <AccordionTrigger className="text-sm font-medium text-left">
                    Can I Tour The Property, And How Do I Confirm My Room Choice?
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600">
                    Virtual tours may be available for some properties. After completing your application and getting approved, you'll be able to confirm your room selection.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {(contactPhone || contactEmail) && (
          <section className="bg-white border-t border-gray-200 py-8 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Get in Touch</h3>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
                {contactPhone && (
                  <a href={`tel:${contactPhone}`} className="flex items-center gap-1.5 hover:text-gray-900" data-testid="link-contact-phone">
                    <Phone className="h-4 w-4" />
                    {contactPhone}
                  </a>
                )}
                {contactEmail && (
                  <a href={`mailto:${contactEmail}`} className="flex items-center gap-1.5 hover:text-gray-900" data-testid="link-contact-email">
                    <Mail className="h-4 w-4" />
                    {contactEmail}
                  </a>
                )}
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="bg-gray-900 text-white py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start justify-between gap-8">
          <div>
            {brandLogoUrl ? (
              <img src={brandLogoUrl} alt={brandName || "Logo"} className="h-8 w-auto brightness-0 invert" />
            ) : brandName ? (
              <span className="text-xl font-bold text-white">{brandName}</span>
            ) : (
              <img src={logoImg} alt="PadPulse" className="h-8 w-auto brightness-0 invert" />
            )}
            <p className="text-gray-400 text-xs mt-2 max-w-xs">
              {footerText}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <p className="font-semibold mb-2">Explore</p>
              <ul className="space-y-1.5 text-gray-400">
                <li><a href="#rooms" className="hover:text-white">Rooms</a></li>
                <li><a href="#faq" className="hover:text-white">FAQ</a></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-2">Company</p>
              <ul className="space-y-1.5 text-gray-400">
                <li><a href="/about" className="hover:text-white">About</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div>
            <Button
              variant="outline"
              size="sm"
              className="bg-transparent border-gray-600 text-white hover:bg-gray-800 text-xs"
              onClick={() => { void openLeadModal(); }}
              data-testid="button-footer-apply"
            >
              {ctaText}
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto border-t border-gray-800 mt-8 pt-4 text-xs text-gray-500">
          &copy; {brandName || "PadPulse"}. All rights reserved.
        </div>
      </footer>

      <Dialog open={showLeadModal} onOpenChange={(open) => { if (!open) setShowLeadModal(false); }}>
        <DialogContent className="max-w-md" data-testid="dialog-lead-form">
          <DialogHeader>
            <DialogTitle>
              {leadSubmitted ? "You're Qualified to Apply!" : "Book a Room Now"}
            </DialogTitle>
          </DialogHeader>

          {leadSubmitted ? (
            <div className="py-4">
              <div className="flex items-center justify-center mb-4">
                <div className="h-14 w-14 rounded-full bg-green-50 flex items-center justify-center">
                  <Check className="h-7 w-7 text-green-600" />
                </div>
              </div>
              <p className="text-center text-sm text-gray-600 mb-6">
                Thanks, <strong>{leadForm.name}</strong>! You're ready to apply through PadSplit.
                Choose your room and complete your free application today.
              </p>
              <Button
                className="w-full"
                style={{ backgroundColor: accentColor, borderColor: accentColor }}
                onClick={() => window.open(referralLink, "_blank")}
                data-testid="button-choose-room-apply"
              >
                Choose Your Room and Apply Now
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              <p className="text-xs text-gray-400 text-center mt-3">
                You'll be redirected to PadSplit to complete your application.
              </p>
            </div>
          ) : (
            <div className="py-2 space-y-4">
              <p className="text-sm text-gray-600">
                Fill in your details to get started. Rooms are listed via PadSplit — after submitting, you'll be directed to their platform to apply.
              </p>
              <div>
                <Label className="text-sm flex items-center gap-1.5 mb-1">
                  <User className="h-3.5 w-3.5" />
                  Full Name
                </Label>
                <Input
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  placeholder="Jane Smith"
                  data-testid="input-lead-name"
                />
              </div>
              <div>
                <Label className="text-sm flex items-center gap-1.5 mb-1">
                  <Mail className="h-3.5 w-3.5" />
                  Email Address
                </Label>
                <Input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                  placeholder="jane@example.com"
                  data-testid="input-lead-email"
                />
              </div>
              <div>
                <Label className="text-sm flex items-center gap-1.5 mb-1">
                  <Phone className="h-3.5 w-3.5" />
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  placeholder="(555) 000-0000"
                  data-testid="input-lead-phone"
                />
              </div>
              <Button
                className="w-full"
                style={{ backgroundColor: accentColor, borderColor: accentColor }}
                onClick={handleLeadSubmit}
                disabled={leadMutation.isPending || !leadForm.name || !leadForm.email || !leadForm.phone}
                data-testid="button-submit-lead"
              >
                {leadMutation.isPending ? "Submitting..." : "Submit & Continue to Apply"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
